import { SELECTED_TAGS_KEY } from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import type { Vault } from '@/types/vault'
import { getCategoryColor } from '@/utils/category/getCategoryColor'
import { getCategoryIcon } from '@/utils/category/getCategoryIcon'
import type { PopoverProps } from '@radix-ui/react-popover'
import { useMemo } from 'react'
import { AccordionItem } from '../common/accordion'
import { FilterAccordionContent, FilterAccordionTrigger, FilterSelect } from './filterSelect'
import { FilterListContent } from './filterSelectContent'

interface Props {
  vaults: Vault[]
  className?: string
}

const MAX_VISIBLE_ICONS = 2

function renderCategoryTriggerIcons(selectedItems: string[]) {
  const visible = selectedItems.slice(0, MAX_VISIBLE_ICONS)
  const overflow = selectedItems.length - MAX_VISIBLE_ICONS
  return (
    <div className="flex items-center gap-1 text-accent">
      {visible.map((tag) => {
        const Icon = getCategoryIcon(tag)
        const color = getCategoryColor(tag)
        return <Icon key={tag} size={16} style={{ color: `rgb(var(${color}))` }} />
      })}
      {overflow > 0 && <span>+{overflow}</span>}
    </div>
  )
}

function renderCategoryItemPrefix(item: string) {
  const Icon = getCategoryIcon(item)
  const color = getCategoryColor(item)
  return <Icon size={16} style={{ color: `rgb(var(${color}))` }} />
}

function useCategoryFilterData(vaults: Vault[]) {
  const { selectedTags, updateSearchState } = useVaultsSearchState()
  const tags = useMemo(() => [...new Set(vaults.flatMap((vault) => vault.tags))].sort(), [vaults])
  const { trackEvent } = useMixpanel()

  return {
    items: tags,
    selectedItems: selectedTags,
    setSelectedItems: (value: string[]) => {
      updateSearchState([SELECTED_TAGS_KEY, value])
      trackEvent('Filter used', { Filter: 'category', Value: value.join(', ') })
    },
    name: 'category',
    renderItemPrefix: renderCategoryItemPrefix,
  }
}

export function CategoryFilter({ vaults, className, modal }: Props & Pick<PopoverProps, 'modal'>) {
  const props = useCategoryFilterData(vaults)
  return (
    <FilterSelect
      {...props}
      renderTriggerContent={renderCategoryTriggerIcons}
      scrollClassName="max-h-56"
      modal={modal}
      className={className}
    />
  )
}

export function CategoryFilterAccordion({ vaults }: Props) {
  const props = useCategoryFilterData(vaults)
  return (
    <AccordionItem value="category">
      <FilterAccordionTrigger name="category" />
      <FilterAccordionContent>
        <FilterListContent {...props} scrollClassName="max-h-48" />
      </FilterAccordionContent>
    </AccordionItem>
  )
}
